import { FC, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Button from 'src/components/ui/Button/Button';
import DeviceParameters from 'src/components/ui/DeviceParameters/DeviceParameters';
import Fans from 'src/components/ui/Fans/Fans';
import SensorData from 'src/components/ui/HeatingApplianceDetails/SensorData/SensorData';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import ShadowBox from 'src/components/ui/ShadowBox/ShadowBox';
import { addModal, removeModal } from 'src/features/globalSlice';
import { routes } from 'src/routes';
import {
  changeParameters,
  getDeviceDetails,
  setDeviceDetails,
} from 'src/store/actions/deviceActions';
import {
  getTicketDetails,
  setTicketDetails,
} from 'src/store/actions/ticketActions';
import { DeviceValue } from 'src/store/models/Device';
import { ModalType } from 'src/store/models/Modal';
import { TicketStatus } from 'src/store/models/Ticket';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';

import classes from './EditParameters.module.scss';

const EditParameters: FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const methods = useForm<{ parameters: DeviceValue[] }>({ mode: 'onChange' });

  const ticket = useAppSelector((state) => state.ticket.ticketDetails);
  const device = useAppSelector((state) => state.device);
  const loadingTicket = useLoadingSelector({
    actionType: getTicketDetails.toString(),
  });
  const loadingDevice = useLoadingSelector({
    actionType: getDeviceDetails.toString(),
  });

  useEffect(() => {
    if (!params?.ticketId) {
      navigate(routes.TICKETS, { replace: true });
    } else {
      dispatch(setTicketDetails(null));
      dispatch(setDeviceDetails(null));
      dispatch(getTicketDetails({ id: params?.ticketId }));
    }
    return () => {
      dispatch(setTicketDetails(null));
      dispatch(setDeviceDetails(null));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timer;
    if (ticket?.unitId) {
      if (ticket?.status === TicketStatus.IN_PROGRESS) {
        const getDetails = (refresh: boolean) => {
          dispatch(getDeviceDetails({ unitId: ticket?.unitId, refresh }));
        };

        getDetails(false);
        interval = setInterval(() => {
          getDetails(true);
        }, 60000);
      } else {
        navigate(routes.TICKETS, { replace: true });
      }
    }

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [ticket]);

  const onSubmit: SubmitHandler<{ parameters: DeviceValue[] }> = () => {
    const changedParamIds = Object.keys(
      methods?.formState?.dirtyFields?.parameters
    ).map(Number);
    const changedParams: DeviceValue[] = methods
      ?.getValues()
      ?.parameters?.filter((v) => changedParamIds?.includes(v.id));

    dispatch(
      addModal({
        title: t('common:are_you_sure'),
        body: t('device_parameters:review_changes'),
        data: changedParams,
        modalType: ModalType.EDIT_PARAMETERS,
        loadingAction: changeParameters?.type,
        primaryButtonText: t('common:confirm_changes'),
        secondaryButtonText: t('common:back_editing'),
        primaryAction: () => {
          dispatch(
            changeParameters({
              ticket: ticket,
              parameters: changedParams,
            })
          );
        },
        secondaryAction: () => {
          dispatch(removeModal());
        },
      })
    );
  };

  const isButtonDisabled =
    !methods?.formState?.isDirty ||
    !!methods?.formState?.errors?.parameters ||
    !device?.deviceDetails ||
    !device?.parameters?.length;

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        className={classes.Container}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <PageHeader
          title={`${t('device:edit_parameters_for')} ${ticket?.model || ''}`}
          showBackButton
          backConfirmationText={
            methods?.formState?.isDirty &&
            t('device_parameters:unsaved_parameters')
          }
          endNode={
            <Button
              type="submit"
              className={classes.HeaderButton}
              disabled={isButtonDisabled}
            >
              {t('device:write_changes')}
            </Button>
          }
        />
        <h4>{t('tickets:appliance_details')}</h4>
        <section>
          <DeviceParameters loading={loadingTicket || loadingDevice} />
          <aside>
            <ShadowBox>
              <Fans
                gaugeSize={65}
                className={classes.Fans}
                loading={loadingTicket || loadingDevice}
                disabled={ticket?.status !== TicketStatus.IN_PROGRESS}
              />
            </ShadowBox>
          </aside>
        </section>
        <section>
          <ShadowBox>
            <SensorData
              classes={{
                loading: classes.SensorLoading,
                root: classes.Sensors,
              }}
              deviceDetails={
                ticket?.status === TicketStatus.IN_PROGRESS &&
                device.deviceDetails
              }
              loading={loadingTicket || loadingDevice}
            />
          </ShadowBox>
        </section>
      </form>
    </FormProvider>
  );
};

export default EditParameters;
